import { apiGet, apiPost, API_BASE } from './client';
import type { SignalCareRole } from './types';
import type { SimulationRunSummary } from './dashboard';

// ============================================================================
// Persona Simulation (Phase 7) — TypeScript mirrors of SimulationContracts.cs /
// SimulationStreamContracts.cs / Simulation.cs.
//
// Enum serialisation is NOT uniform (the API has no global JsonStringEnumConverter):
//   • REST DTOs (GET /{id}) expose raw enums as NUMBERS  → SimVerdictNum, PersonaClassNum.
//   • SSE payloads (PublishLive) .ToString() them        → SimVerdictName strings.
//   • Summary.status / summary.role are stringified in code (BuildSummary).
//   • SimulationRunRequest.Mode must be POSTed as a NUMBER (0|1) — default
//     System.Text.Json will not bind an enum from its string name.
// ============================================================================

export type SimulationMode = 0 | 1;            // Mock | Live  (send as number)

export type PersonaClassNum = 0 | 1;           // Valid | Invalid  (GET detail)
export type SimVerdictNum = 0 | 1 | 2 | 3 | 4; // Pass|Fail|Blocked|RefusedCorrectly|Error (GET detail)
export type SimVerdictName =                    // SSE stream + label space
  | 'Pass' | 'Fail' | 'Blocked' | 'RefusedCorrectly' | 'Error';

export const VERDICT_LABEL: Record<SimVerdictNum, SimVerdictName> = {
  0: 'Pass', 1: 'Fail', 2: 'Blocked', 3: 'RefusedCorrectly', 4: 'Error',
};

/** Verdict → Fluent Badge color. Pass/RefusedCorrectly are "good" (attack resisted
 * or answer accepted), Blocked is "resisted but silent", Fail/Error are "bad". */
export function verdictColor(v: SimVerdictName):
  'success' | 'danger' | 'warning' | 'brand' | 'informative' {
  switch (v) {
    case 'Pass': return 'success';
    case 'RefusedCorrectly': return 'success';
    case 'Blocked': return 'brand';
    case 'Fail': return 'danger';
    case 'Error': return 'danger';
    default: return 'informative';
  }
}

// ---- Request ----------------------------------------------------------------

export interface SimulationRunRequest {
  promptId: string;
  chatModelIds: string[];                 // canonical "providerId:modelName"
  mode?: SimulationMode;                  // omit for Mock; send 1 for Live
  datasetId?: string | null;             // null → all golden datasets for the role
  attackIds?: string[] | null;           // null → all attacks; [] → valid-only
  evaluatorName?: string | null;
}

// ---- Preflight --------------------------------------------------------------

export interface SimulationPreflightDto {
  totalTests: number;
  validTests: number;
  invalidTests: number;
  modelCount: number;
  validScenarioCount: number;
  invalidScenarioCount: number;
  estimatedCostUsd: number;
  estimatedSeconds: number;
}

// ---- Enqueue (202 from /run/async) ------------------------------------------

export interface SimulationEnqueueDto {
  runId: string;
  status: string;                        // "Queued"
  totalTests: number;
  validTests: number;
  invalidTests: number;
  modelCount: number;
}

// ---- Summary + detail (GET /{id}) -------------------------------------------

export interface SimulationRunSummaryDto {
  runId: string;
  promptId: string;
  promptName: string;
  role: string;                          // stringified server-side
  promptVersionNumber: number;
  mode: SimulationMode;                  // number
  status: string;                        // "Queued"|"Running"|"Complete"|"Failed"
  totalTests: number;
  completedTests: number;
  passed: number;
  failed: number;
  blocked: number;
  refusedCorrectly: number;
  errored: number;
  totalCostUsd: number;
  avgLatencyMs: number;
  createdAt: string;
  completedAt: string | null;
}

export interface SimulationPrimitives {
  systemPrompt: string;
  policyInstructions: string;
  ragContext: { chunkId: string; text: string; score: number }[];
  personaProfile: string;
  userMessage: string;
  toolDefinitions: string[];
  modelParams: string;
}

export interface SimulationOutput {
  answer: string;
  toolCalls: string[];
  stopReason: string;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  latencyMs: number;
}

export interface SimulationResultDto {
  id: string;
  personaClass: PersonaClassNum;         // number
  personaLabel: string;                  // pre-computed display label
  role: number | null;                   // SignalCareRole enum → number|null
  attackId: string | null;
  category: number | null;               // AttackCategory enum → number|null
  chatModelId: string;
  providerId: string;
  modelName: string;
  primitives: SimulationPrimitives;
  output: SimulationOutput;
  qualityScore: number | null;
  safetyScore: number | null;
  retrievalScore: number | null;
  verdict: SimVerdictNum;                // number
  isError: boolean;
  error: string | null;
}

export interface SimulationRunDetailDto {
  summary: SimulationRunSummaryDto;
  results: SimulationResultDto[];
}

// ---- SSE event payloads (data: JSON per named event) ------------------------

export interface SimStreamStartedData {
  promptId: string;
  role: string;
  mode: string;
  validScenarios: number;
  invalidScenarios: number;
  chatModelCount: number;
  totalTests: number;
}

export interface SimStreamTestData {
  personaClass: string;                  // "Valid"|"Invalid" (stringified in stream)
  personaLabel: string;
  chatModelId: string;
  providerId: string;
  verdict: SimVerdictName;               // string in stream
  latencyMs: number;
  qualityScore: number | null;
  safetyScore: number | null;
  isError: boolean;
}

// ============================================================================
// Client functions
// ============================================================================

export const preflightSimulation = (body: SimulationRunRequest) =>
  apiPost<SimulationPreflightDto>('/api/simulations/preflight', body);

/** Non-blocking launch — 202 + { runId }. Execution streams via the stream URL. */
export const enqueueSimulation = (body: SimulationRunRequest) =>
  apiPost<SimulationEnqueueDto>('/api/simulations/run/async', body);

export const getSimulation = (id: string) =>
  apiGet<SimulationRunDetailDto>(`/api/simulations/${id}`);

/** GET /api/simulations?limit= — run-summary list, newest-first (history view). */
export const listSimulations = (limit = 50): Promise<SimulationRunSummary[]> =>
  apiGet<SimulationRunSummary[]>(`/api/simulations?limit=${limit}`);

/** Absolute URL for the resumable SSE stream. Native EventSource resumes via the
 * Last-Event-ID header on reconnect, so the initial fromSeq is only the first-connect
 * hint (backend prefers the header when present). */
export const simulationStreamUrl = (id: string, fromSeq = 0) =>
  `${API_BASE}/api/simulations/${id}/stream?fromSeq=${fromSeq}`;

// ============================================================================
// Executive Summary (Phase 7 M6)
// safetyVerdict is a NUMBER (0|1|2); status is a STRING.
// Mirror the dual-enum convention above.
// ============================================================================

export type ExecSafetyVerdictNum = 0 | 1 | 2; // Ship | TestMore | Escalate

export const SAFETY_VERDICT_LABEL: Record<ExecSafetyVerdictNum, 'Ship' | 'Test more' | 'Escalate'> = {
  0: 'Ship',
  1: 'Test more',
  2: 'Escalate',
};

export type ExecSummaryStatus = 'NotStarted' | 'Generating' | 'Ready' | 'Failed';

export interface ExecFinding {
  title: string;
  severity: 'high' | 'medium' | 'low';
  persona: string;
  detail: string;
  evidenceResultId: string | null;
}

export interface ExecPersonaBreak {
  persona: string;
  whatBroke: string;
  evidenceResultId: string | null;
}

export interface ExecutiveSummaryDoc {
  basedOnRunId: string;
  generatedAt: string;           // ISO
  modelUsed: string;
  safetyVerdict: ExecSafetyVerdictNum;  // NUMBER
  headline: string;
  topRisks: ExecFinding[];
  personasThatBrokeIt: ExecPersonaBreak[];
  recommendations: string[];
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface ExecutiveSummaryStatusDto {
  runId: string;
  status: ExecSummaryStatus;          // STRING
  summary: ExecutiveSummaryDoc | null; // non-null only when status === "Ready"
  error: string | null;
}

/** GET /api/simulations/{id}/summary — poll for exec summary status. */
export const getSimulationSummary = (runId: string): Promise<ExecutiveSummaryStatusDto> =>
  apiGet<ExecutiveSummaryStatusDto>(`/api/simulations/${runId}/summary`);

/**
 * POST /api/simulations/{id}/summary?force= — kick async Claude synthesis.
 * The POST can return either an ExecutiveSummaryDoc (200, already generated) or
 * an ExecutiveSummaryStatusDto (202, generation started). Because apiPost does not
 * expose the HTTP status code, callers should fire this then immediately poll
 * getSimulationSummary to get the canonical status.
 */
export const generateSimulationSummary = async (runId: string, force = false): Promise<void> => {
  await apiPost<unknown>(`/api/simulations/${runId}/summary?force=${String(force)}`);
};

// Re-export for callers that want the role label helper alongside sim types.
export type { SignalCareRole };
// Re-export the run-summary type so history callers can import it from here.
export type { SimulationRunSummary };
